import { ReactNode, useMemo, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { MediaFullScreen } from "../../../../common/components/media/MediaFullScreen";
import { customRound } from "../../../../common/helpers/customRound";
import StyledLoadingButton from "../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { RECORDING_PAGE_SIZE } from "../../utils/constants";
import { DEFAULT_MEDIA_ICON_STYLES } from "../../../../common/variables/common";

export type ExpandableData = { label: string; value: ReactNode };

export type EventRecords = {
  url: string;
  timestamp?: string;
  rateMCF?: string;
};

interface ExpandableEventDetailsProps {
  colSpan: number;
  records: RecordingViewModel[];
  expandableData: ExpandableData[];
  latestRecordingTimestamp?: string;
  summary?: string;
  showSummary?: boolean;
  showRateMCF?: boolean;
  fetchNextPage: () => Promise<void>;
  shouldLoadMore?: boolean;
  totalRecordings?: number;
  recordingsLoading: boolean;
}

const ExpandableEventDetails = ({
  colSpan,
  records,
  expandableData,
  latestRecordingTimestamp,
  summary,
  showSummary,
  fetchNextPage,
  showRateMCF,
  totalRecordings = 0,
  recordingsLoading,
}: ExpandableEventDetailsProps) => {
  const theme = useTheme();

  const [expanded, setExpanded] = useState(false);

  const [showAllRecordings, setShowAllRecordings] = useState(false);

  const handleExpand = () => {
    setExpanded(v => !v);
  };

  const visibleRecordings = useMemo(() => {
    return showAllRecordings ? records : records.slice(0, RECORDING_PAGE_SIZE);
  }, [records, showAllRecordings]);

  const handleLoadMore = async () => {
    await fetchNextPage();

    setShowAllRecordings(true);
  };

  const handleLoadLess = () => {
    setShowAllRecordings(false);
  };

  const shouldShowLoadMore =
    !showAllRecordings && totalRecordings > RECORDING_PAGE_SIZE;

  const shouldShowLoadLess =
    showAllRecordings && totalRecordings > RECORDING_PAGE_SIZE;

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{
            border: 0,
            p: 0,
            background: theme.palette.otherBackgroundLight.main,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              py: "8px",
              pl: "16px",
            }}
          >
            <IconButton size="medium" sx={{ p: 0 }} onClick={handleExpand}>
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  color: theme.palette.otherTextTertiary.main,
                }}
              />
            </IconButton>
            <Typography
              variant="body2Regular"
              sx={{
                ml: "8px",
                color: theme.palette.text.secondary,
              }}
            >
              {totalRecordings > 0
                ? `${totalRecordings} Recording${
                    totalRecordings > 1 ? "s" : ""
                  }`
                : "No Recordings"}
            </Typography>
            <Typography
              variant="body2Regular"
              sx={{
                ml: "98px",
                color: theme.palette.text.secondary,
              }}
            >
              First Recorded: {latestRecordingTimestamp}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{
            p: 0,
            border: 0,
            background: theme.palette.otherBackgroundLight.main,
          }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: "16px",
                py: "12px",
                mx: "16px",
                mb: "16px",
                borderRadius: "0.625rem",
                background: theme.palette.otherBackground.main,
              }}
            >
              <Stack flexDirection="row" gap="12em">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    gap: "0.5em",
                  }}
                >
                  {expandableData.map(item => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1em",
                      }}
                    >
                      <Typography
                        variant="body2Regular"
                        fontWeight="fontWeightMedium"
                        color="text.secondary"
                        sx={{ minWidth: 188 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="body2Regular">
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {showSummary && (
                  <Box display="flex" flexDirection="column" gap="0.5em">
                    <Typography
                      variant="body2Regular"
                      fontWeight="fontWeightMedium"
                      color="text.secondary"
                    >
                      SUMMARY
                    </Typography>
                    <Typography variant="body2Regular">
                      {summary || "No description provided."}
                    </Typography>
                  </Box>
                )}
              </Stack>
              {records.length > 0 && (
                <>
                  <Divider sx={{ my: "16px" }} />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {visibleRecordings.map(
                      ({
                        id,
                        rawEventInfo,
                        timestamp,
                        keyFrame,
                        mediaOutput,
                      }) => {
                        const rateMCF = showRateMCF
                          ? customRound(
                              +(rawEventInfo?.data?.rate_mcf ?? 0) * 86400
                            )
                          : 0;

                        return (
                          <Box
                            key={id}
                            sx={{
                              display: "flex",
                              gap: "8px",
                              position: "relative",
                            }}
                          >
                            <MediaFullScreen
                              keyFrame={keyFrame}
                              mediaOutput={mediaOutput}
                              s3ImageFullScreenProps={{
                                sx: DEFAULT_MEDIA_ICON_STYLES,
                                fixedHeight: "75px",
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                flexGrow: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "4px",
                                }}
                              >
                                <Typography
                                  variant="body2Regular"
                                  fontWeight="fontWeightMedium"
                                  color="text.secondary"
                                  sx={{ minWidth: 100 }}
                                >
                                  Timestamp
                                </Typography>
                                <Typography variant="body2Regular">
                                  {formatTimestamp(timestamp)}
                                </Typography>
                              </Box>
                              {showRateMCF && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    mt: "8px",
                                  }}
                                >
                                  <Typography
                                    variant="body2Regular"
                                    fontWeight="fontWeightMedium"
                                    color="text.secondary"
                                    sx={{ minWidth: 100 }}
                                  >
                                    Rate MCF/D
                                  </Typography>
                                  <Typography variant="body2Regular">
                                    {rateMCF}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      }
                    )}
                  </Box>

                  {shouldShowLoadMore && (
                    <>
                      <Divider sx={{ my: "16px" }} />
                      <StyledLoadingButton
                        loading={recordingsLoading}
                        variant="outlined"
                        onClick={handleLoadMore}
                      >
                        Load more
                      </StyledLoadingButton>
                    </>
                  )}

                  {shouldShowLoadLess && (
                    <>
                      <Divider sx={{ my: "16px" }} />
                      <StyledLoadingButton
                        variant="outlined"
                        onClick={handleLoadLess}
                      >
                        <Typography variant="buttonMedium">
                          Load Less
                        </Typography>
                      </StyledLoadingButton>
                    </>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ExpandableEventDetails;
